import { z } from "zod";
import {
  ConversationCompletionDispositionSchema,
  ConversationIdSchema,
  ToolActivityOutcomeSchema,
} from "~/shared/agent-core/conversation-contract";
import {
  ActionProposalArtifactSchema,
  ActionProposalItemStatusSchema,
  ActionProposalStatusSchema,
  ActionReceiptSummarySchema,
} from "~/shared/ai/assistant-action-artifact";
import type { AgentEvent } from "~/shared/ai/assistant-event-contract";
import { REPORT_FIELD_KINDS, ReportArtifactCommitSchema } from "~/utils/types/assistant-report-artifact";

const DebugPayloadSchema = z
  .object({
    timestamp: z.string(),
    stages: z.array(
      z
        .object({
          name: z.string(),
          status: z.enum(["success", "error", "skipped"]),
          durationMs: z.number(),
          input: z.unknown().optional(),
          output: z.unknown().optional(),
          error: z.string().optional(),
        })
        .strict(),
    ),
    totalDurationMs: z.number(),
  })
  .strict();

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const KNOWN_REPORT_FIELD_KINDS = new Set<string>(REPORT_FIELD_KINDS);

/** A report field kind this browser does not know yet reads as text; its values are unchanged. */
const withKnownReportFieldKinds = (commit: unknown): unknown => {
  if (!isRecord(commit) || !isRecord(commit.dataset) || !Array.isArray(commit.dataset.fields)) {
    return commit;
  }
  const fields = commit.dataset.fields.map(field =>
    isRecord(field) && typeof field.semanticType === "string" && !KNOWN_REPORT_FIELD_KINDS.has(field.semanticType)
      ? { ...field, semanticType: "text" }
      : field,
  );
  return { ...commit, dataset: { ...commit.dataset, fields } };
};

const hasOnlyKeys = (value: Record<string, unknown>, allowed: string[]): boolean => Object.keys(value).every(key => allowed.includes(key));

export const parseAgentEvent = (value: unknown): AgentEvent | undefined => {
  if (!isRecord(value) || typeof value.type !== "string") {
    return undefined;
  }
  if (value.type === "status" && hasOnlyKeys(value, ["type", "message"]) && typeof value.message === "string") {
    return { type: "status", message: value.message };
  }
  if (value.type === "phase" && hasOnlyKeys(value, ["type", "phase"]) && value.phase === "reviewing_results") {
    return { type: "phase", phase: "reviewing_results" };
  }
  if (
    value.type === "phase" &&
    value.phase === "preparing_attachments" &&
    hasOnlyKeys(value, ["type", "phase", "state", "fileNames", "failedFileNames"])
  ) {
    const parsed = z
      .object({
        type: z.literal("phase"),
        phase: z.literal("preparing_attachments"),
        state: z.enum(["started", "completed", "failed", "stopped"]),
        fileNames: z.array(z.string().min(1).max(255)).max(5),
        failedFileNames: z.array(z.string().min(1).max(255)).max(5),
      })
      .strict()
      .safeParse(value);
    return parsed.success ? parsed.data : undefined;
  }
  if (value.type === "content" && hasOnlyKeys(value, ["type", "text"]) && typeof value.text === "string") {
    return { type: "content", text: value.text };
  }
  if (value.type === "error" && hasOnlyKeys(value, ["type", "message"]) && typeof value.message === "string") {
    return { type: "error", message: value.message };
  }
  if (
    value.type === "tool_call" &&
    hasOnlyKeys(value, ["type", "name", "args", "callId"]) &&
    typeof value.name === "string" &&
    isRecord(value.args) &&
    (value.callId === undefined || typeof value.callId === "string")
  ) {
    return {
      type: "tool_call",
      name: value.name,
      args: Object.fromEntries(Object.entries(value.args)),
      ...(typeof value.callId === "string" ? { callId: value.callId } : {}),
    };
  }
  if (
    value.type === "tool_result" &&
    hasOnlyKeys(value, ["type", "name", "outcome", "durationMs", "callId"]) &&
    typeof value.name === "string" &&
    typeof value.durationMs === "number" &&
    Number.isFinite(value.durationMs) &&
    value.durationMs >= 0 &&
    (value.callId === undefined || typeof value.callId === "string")
  ) {
    const outcome = ToolActivityOutcomeSchema.safeParse(value.outcome);
    return outcome.success
      ? {
          type: "tool_result",
          name: value.name,
          outcome: outcome.data,
          durationMs: value.durationMs,
          ...(typeof value.callId === "string" ? { callId: value.callId } : {}),
        }
      : undefined;
  }
  if (value.type === "debug" && hasOnlyKeys(value, ["type", "payload"])) {
    const payload = DebugPayloadSchema.safeParse(value.payload);
    return payload.success ? { type: "debug", payload: payload.data } : undefined;
  }
  if (
    value.type === "action_progress" &&
    hasOnlyKeys(value, ["type", "proposalId", "version", "lifecycleSequence", "completed", "total"])
  ) {
    const parsed = z
      .object({
        type: z.literal("action_progress"),
        proposalId: z.string().uuid(),
        version: z.number().int().min(1),
        lifecycleSequence: z.number().int().nonnegative(),
        completed: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
      })
      .strict()
      .safeParse(value);
    return parsed.success ? parsed.data : undefined;
  }
  if (
    value.type === "action_item_outcome" &&
    hasOnlyKeys(value, ["type", "proposalId", "version", "lifecycleSequence", "itemKey", "status", "message", "resultEntityId"])
  ) {
    const parsed = z
      .object({
        type: z.literal("action_item_outcome"),
        proposalId: z.string().uuid(),
        version: z.number().int().min(1),
        lifecycleSequence: z.number().int().nonnegative(),
        itemKey: z.string().min(1),
        status: ActionProposalItemStatusSchema,
        message: z.string(),
        resultEntityId: z.string().uuid().optional(),
      })
      .strict()
      .safeParse(value);
    return parsed.success ? parsed.data : undefined;
  }
  if (
    value.type === "action_lifecycle" &&
    hasOnlyKeys(value, ["type", "proposalId", "version", "lifecycleSequence", "status", "counts", "resumable", "receipt"])
  ) {
    const parsed = z
      .object({
        type: z.literal("action_lifecycle"),
        proposalId: z.string().uuid(),
        version: z.number().int().min(1),
        lifecycleSequence: z.number().int().nonnegative(),
        status: ActionProposalStatusSchema,
        counts: ActionReceiptSummarySchema.optional(),
        resumable: z.boolean(),
        receipt: ActionReceiptSummarySchema.optional(),
      })
      .strict()
      .safeParse(value);
    return parsed.success ? parsed.data : undefined;
  }
  if (
    value.type !== "done" ||
    !hasOnlyKeys(value, ["type", "threadId", "turnId", "completionDisposition", "reportArtifactCommit", "actionProposalCommit"])
  ) {
    return undefined;
  }
  const conversationId = ConversationIdSchema.safeParse(value.threadId);
  if (!conversationId.success) {
    return undefined;
  }
  const hasAttachment = value.reportArtifactCommit !== undefined || value.actionProposalCommit !== undefined;
  if (value.turnId === undefined && !hasAttachment && value.completionDisposition === undefined) {
    return { type: "done", threadId: conversationId.data };
  }
  if (typeof value.turnId !== "string") {
    return undefined;
  }
  const completionDisposition =
    value.completionDisposition === undefined ? undefined : ConversationCompletionDispositionSchema.safeParse(value.completionDisposition);
  if (completionDisposition && !completionDisposition.success) {
    return undefined;
  }
  if (!hasAttachment) {
    return {
      type: "done",
      threadId: conversationId.data,
      turnId: value.turnId,
      ...(completionDisposition?.success ? { completionDisposition: completionDisposition.data } : {}),
    };
  }
  // A report this browser cannot read is left off; the answer and the turn still complete.
  const reportCommit =
    value.reportArtifactCommit === undefined
      ? undefined
      : ReportArtifactCommitSchema.safeParse(withKnownReportFieldKinds(value.reportArtifactCommit));
  const actionCommit =
    value.actionProposalCommit === undefined ? undefined : ActionProposalArtifactSchema.safeParse(value.actionProposalCommit);
  if (actionCommit && !actionCommit.success) {
    return undefined;
  }
  return {
    type: "done",
    threadId: conversationId.data,
    turnId: value.turnId,
    ...(completionDisposition?.success ? { completionDisposition: completionDisposition.data } : {}),
    ...(reportCommit?.success ? { reportArtifactCommit: reportCommit.data } : {}),
    ...(actionCommit?.success ? { actionProposalCommit: actionCommit.data } : {}),
  };
};
